import { useEffect, useState, useCallback } from 'react';

import { toast } from '@hivebrite/design-system/Toast';

import { axiosInstance as axios } from 'hb-react/shared/utils/axios';

import { useTopicCardContext } from '../contexts/TopicCardContext';
import { useTopicsIndexContext } from '../contexts/TopicsIndexContext';
import handleDisplayErrorToast from '../utils/handleDisplayErrorToast';
import reloadTopics from '../utils/reloadTopics';

const useTopicMemberRequest = (setIsMember) => {
  const {
    currentView,
    fetchCategoryTopics,
    fetchFilteredTopics,
    fetchMyTopics,
    fetchOtherTopics,
    setDisplaySuccessMessageJoinPublicTopic,
  } = useTopicsIndexContext();
  const {
    href,
    forceDefaultLink,
    setDisplayRequestModal,
    target,
    topic,
    updateTopicApproval,
  } = useTopicCardContext();

  const {
    custom_attributes_to_fill: customAttributesToFill,
    automatic_validation: automaticValidation,
    is_public_and_requires_user_to_fill_custom_attributes_to_join:
      hasCustomAttributesAndIsPublic,
  } = topic;

  const [requestApproval, setRequestApproval] = useState({
    id: topic.approval_id,
  });
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isCanceling, setIsCanceling] = useState(false);
  const [isEditing, setIsEditing] = useState(false);

  const isOnAutomaticValidationWithCustomAttributes =
    automaticValidation && customAttributesToFill;

  const handleSubmitRequest = useCallback(
    (values) => {
      setIsSubmitting(true);

      if (requestApproval.id) {
        // Edit request
        axios
          .patch(
            Routes.topics_topic_approval_path(topic.id, requestApproval.id),
            values,
          )
          .then((response) => {
            setRequestApproval(response.data.approval);
            updateTopicApproval(topic.id, response.data.approval.id);
            setIsEditing(false);
            setDisplayRequestModal(false);

            if (response.data.approval.status === 'approved') {
              setDisplaySuccessMessageJoinPublicTopic({
                forceDefaultLink,
                href,
                isDisplayed: true,
                name: topic.name,
                target,
              });
            } else {
              toast.success(
                __('Request to join has been successfully updated'),
              );
            }
          })
          .catch((error) => handleDisplayErrorToast(error))
          .finally(() => setIsSubmitting(false));
      } else {
        // New request

        axios
          .post(Routes.topics_topic_approvals_path(topic.id), values)
          .then((response) => {
            const topicApproval = response.data.approval;

            const shouldShowSuccessDirectly =
              !!topicApproval.created_at &&
              !!topicApproval.auto_approval_user_list_id &&
              topicApproval.status === 'approved';

            reloadTopics({
              currentView,
              fetchCategoryTopics,
              fetchFilteredTopics,
              fetchMyTopics,
              fetchOtherTopics,
              setIsMember,
              topic,
            });

            if (
              hasCustomAttributesAndIsPublic ||
              isOnAutomaticValidationWithCustomAttributes ||
              shouldShowSuccessDirectly
            ) {
              setDisplayRequestModal(false);
              setDisplaySuccessMessageJoinPublicTopic({
                forceDefaultLink,
                href,
                isDisplayed: true,
                name: topic.name,
                target,
              });
            } else {
              setRequestApproval(response.data.approval);
              updateTopicApproval(topic.id, response.data.approval.id);
            }
          })
          .catch((error) => handleDisplayErrorToast(error))
          .finally(() => setIsSubmitting(false));
      }
    },
    [requestApproval],
  );

  const handleInitApproval = useCallback(() => {
    const { id, approval_id: approvalId } = topic;
    let approvalPath;

    if (requestApproval.id) {
      approvalPath = Routes.topics_topic_approval_path(id, approvalId);
    } else {
      approvalPath = Routes.new_topics_topic_approval_path(id);
    }

    setIsLoading(true);

    axios
      .get(approvalPath)
      .then((response) => {
        const topicApproval = response.data.approval;

        setRequestApproval(topicApproval);

        if (
          topicApproval.auto_approval_user_list_id ||
          topicApproval.status === 'approved'
        ) {
          updateTopicApproval(
            topic.id,
            topicApproval.id,
            topicApproval.created_at,
          );
        }
      })
      .catch((error) => handleDisplayErrorToast(error))
      .finally(() => setIsLoading(false));
  }, [requestApproval]);

  const handleEdit = useCallback(() => {
    setIsEditing(!isEditing);
  }, [isEditing]);

  const handleCancel = useCallback(
    (displaySuccessNotification = true) => {
      setIsCanceling(true);

      if (requestApproval.id) {
        axios
          .delete(
            Routes.topics_topic_approval_path(topic.id, requestApproval.id),
          )
          .then(() => {
            setRequestApproval({});
            updateTopicApproval(topic.id, undefined);
            setIsEditing(false);
            setDisplayRequestModal(false);
            displaySuccessNotification &&
              toast.success(__('Successfully canceled'));
          })
          .catch((error) => handleDisplayErrorToast(error))
          .finally(() => setIsCanceling(false));
      } else {
        setDisplayRequestModal(false);
        displaySuccessNotification &&
          toast.success(__('Successfully canceled'));
      }
    },
    [topic, requestApproval],
  );

  useEffect(() => {
    handleInitApproval();
  }, []);

  return {
    handleCancel,
    handleEdit,
    handleSubmitRequest,
    isEditing,
    isLoading,
    isSubmitting,
    isCanceling,
    requestApproval,
  };
};

export default useTopicMemberRequest;
